"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import ScrollReveal from "../common/ScrollReveal";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { WHEEL_LABEL_SRC, WHEEL_PANEL_PATHS, WHEEL_VIEW_BOX } from "./hackgridWheelPaths";

type Corner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

const CORNERS: Corner[] = ["topLeft", "topRight", "bottomLeft", "bottomRight"];

interface FeaturePanel {
  title: string;
  description: string;
}

const FEATURES: {
  topLeft: FeaturePanel;
  topRight: FeaturePanel;
  bottomLeft: FeaturePanel;
  bottomRight: FeaturePanel;
} = {
  topLeft: {
    title: "TRAINING MANAGEMENT SYSTEMS",
    description:
      "HackGrid lets instructors and participants interact with the underlying components of the Cyber Range through a single, user-friendly interface.",
  },
  topRight: {
    title: "COMPREHENSIVE CHALLENGES",
    description:
      "Extensive library of CTFs, coding tasks, and hands-on labs across foundational, web, red team, and OT/ICS domains—designed to build and test real-world cybersecurity skills at scale.",
  },
  bottomLeft: {
    title: "POWERFUL ORCHESTRATION",
    description:
      "HackGrid enables dynamic network-topology creation and host provisioning through drag-and-drop image libraries and templates across four orchestration types.",
  },
  bottomRight: {
    title: "FLEXIBLE SCENARIOS",
    description:
      "Customizable scenarios and network diagrams with deployable vulnerabilities and live malware to train and test blue teams in forensics, threat hunting, and incident response.",
  },
};

function FeatureCard({
  title,
  description,
  active,
  onActivate,
  onDeactivate,
  onToggle,
}: FeaturePanel & {
  active: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onToggle: () => void;
}) {
  return (
    <div
      tabIndex={0}
      onMouseEnter={onActivate}
      onMouseLeave={onDeactivate}
      onFocus={onActivate}
      onBlur={onDeactivate}
      onClick={onToggle}
      /* Stacked cards run full width and are only as tall as their text; the
         421px cap and the 266px floor are what line the two desktop columns up
         either side of the wheel. Below sm the padding drops to the same 20px
         rung ExpandCollapseCard uses so the Home cards match. */
      className="flex w-full flex-col items-start gap-4 rounded-[8px] bg-gradient-to-t from-background to-surface-raised p-5 drop-shadow-[-16px_21px_14px_rgba(0,0,0,0.15)] outline-none transition-colors duration-300 sm:p-8 lg:min-h-[266px] lg:max-w-[421px]"
    >
      <div
        className={`flex w-full items-center border-b-[0.5px] border-solid py-2 transition-colors duration-300 ${
          active ? "border-primary" : "border-text-highlight"
        }`}
      >
        <p
          className={`m-0 flex-1 font-[family-name:var(--font-avenir)] text-[16px] font-medium uppercase tracking-[0.15px] transition-colors duration-300 sm:text-[18px] ${
            active ? "text-primary" : "text-text-highlight"
          }`}
        >
          {title}
        </p>
      </div>
      <p className="body-1 m-0 w-full text-text-highlight">{description}</p>
    </div>
  );
}

// Each corner uses its own viewBox + path from Figma (1514:57363/4/73/74) — the
// four connectors are distinct shapes, not one reused path.
//
// Two things changed from the Figma export:
//
//  - The upper pair is the lower pair mirrored about the artwork's centre line,
//    where Figma had them as their own longer shape: an 84px horizontal run into
//    a 74px dive at 45°, which put the join ~20px *under* the white ring while
//    the bottom two stopped outside it. Mirroring is what keeps the two ends of
//    the same line honest — same 85px bar, same ~33px stub, and the same radius
//    off centre (222/216px), which matters because the ring's outer edge is not
//    a constant: it runs 210px at the crown but 215-217px across the diagonal
//    shoulders where these four land, so a join that clears the crown can still
//    cut into the band up there. Sitting where the bottom pair sits clears it at
//    every angle of the spin.
//  - All four are placed in px off the centre instead of in % of this grid. The
//    grid box grows taller as the cards reflow (609px at 1440, 939px at 1024),
//    so percentage insets slid the connectors 60-100px away from a wheel that is
//    always a fixed 426px — they only lined up at 1440. The wheel is exactly
//    centred in the overlay at every lg width (row-span-2 + items-center), so
//    `calc(50% ± px)` anchors each box to the artwork itself: -213px is the
//    wheel's own edge, and the offsets after it are measured from there. The
//    viewBox is then 1:1 with the box, so the diagonals stay at a true 45°.
const CONNECTORS: {
  corner: Corner;
  style: React.CSSProperties;
  viewBox: string;
  d: string;
}[] = [
  {
    corner: "topLeft",
    style: {
      left: "calc(50% - 232px)",
      top: "calc(50% - 209.8px)",
      width: "109.2px",
      height: "22px",
    },
    viewBox: "0 0 109.2 22",
    d: "M0.5 0.5H87L108.7 21.5",
  },
  {
    corner: "topRight",
    style: {
      left: "calc(50% + 118.3px)",
      top: "calc(50% - 209.8px)",
      width: "111.8px",
      height: "26.5px",
    },
    viewBox: "0 0 111.8 26.5",
    d: "M111.3 0.5H26.7L0.5 26",
  },
  {
    corner: "bottomLeft",
    style: {
      left: "calc(50% - 232px)",
      top: "calc(50% + 181.2px)",
      width: "109.2px",
      height: "22px",
    },
    viewBox: "0 0 109.2 22",
    d: "M0.5 21.5H87L108.7 0.5",
  },
  {
    corner: "bottomRight",
    style: {
      left: "calc(50% + 118.3px)",
      top: "calc(50% + 176.7px)",
      width: "111.8px",
      height: "26.5px",
    },
    viewBox: "0 0 111.8 26.5",
    d: "M111.3 26H26.7L0.5 0.5",
  },
];

function ConnectorOverlay({ hovered }: { hovered: Corner | null }) {
  return (
    <div className="pointer-events-none absolute inset-0 hidden lg:block">
      {CONNECTORS.map(({ corner, style, viewBox, d }) => {
        const active = hovered === corner;
        return (
          <div key={corner} className="absolute" style={style}>
            <svg
              preserveAspectRatio="none"
              viewBox={viewBox}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                overflow: "visible",
              }}
            >
              <path
                d={d}
                stroke={active ? "#ec1938" : "#ffffff"}
                strokeOpacity={active ? 1 : 0.3}
                strokeMiterlimit={10}
                style={{
                  transition: "stroke 0.3s ease, stroke-opacity 0.3s ease",
                }}
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
}

// The wheel artwork (Hackgrid_core.svg) goes through next/image, so the four
// sector panels inside it are just pixels — nothing in there can be hovered,
// clicked or recoloured. This puts the interaction back on top of it in two
// layers (both generated by scripts/extract-hackgrid-wheel-labels.mjs):
//
//   - one <Image> per sector holding a red copy of that sector's label, faded in
//     over the white text baked into the artwork. It has to be an <img> like the
//     artwork itself: Chrome rounds an SVG-in-<img> to whole device pixels, so an
//     inline <svg> lands up to ~0.5 units off and leaves a white hairline down
//     one side of every glyph. Same box + same element type = same rounding.
//   - a transparent hit path per panel silhouette, which does need to be inline
//     to carry the handlers, and where half a unit of slop is invisible.
function WheelSectors({
  active,
  onActivate,
  onDeactivate,
  onToggle,
}: {
  active: Corner | null;
  onActivate: (corner: Corner) => void;
  onDeactivate: (corner: Corner) => void;
  onToggle: (corner: Corner) => void;
}) {
  return (
    <>
      {CORNERS.map((corner) => (
        <Image
          key={`label-${corner}`}
          src={WHEEL_LABEL_SRC[corner]}
          alt=""
          aria-hidden
          fill
          className="pointer-events-none object-contain transition-opacity duration-300"
          style={{ opacity: active === corner ? 1 : 0 }}
        />
      ))}

      <svg
        viewBox={WHEEL_VIEW_BOX}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        {CORNERS.map((corner) => (
          <path
            key={`hit-${corner}`}
            d={WHEEL_PANEL_PATHS[corner]}
            fill="transparent"
            role="button"
            tabIndex={0}
            aria-label={FEATURES[corner].title}
            style={{ pointerEvents: "all", cursor: "pointer", outline: "none" }}
            onMouseEnter={() => onActivate(corner)}
            onMouseLeave={() => onDeactivate(corner)}
            onFocus={() => onActivate(corner)}
            onBlur={() => onDeactivate(corner)}
            onClick={() => onToggle(corner)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle(corner);
              }
            }}
          />
        ))}
      </svg>
    </>
  );
}

export default function HackGrid() {
  const [hovered, setHovered] = useState<Corner | null>(null);
  // Click/tap pins a sector so the highlight survives the pointer leaving (and
  // works at all on touch, where there is no hover).
  const [pinned, setPinned] = useState<Corner | null>(null);
  const active = hovered ?? pinned;
  const isDesktop = useIsDesktop();
  const cardRefs = useRef<Partial<Record<Corner, HTMLDivElement | null>>>({});
  const wheelRingRef = useRef<HTMLDivElement>(null);
  const activate = (corner: Corner) => setHovered(corner);
  const deactivate = (corner: Corner) => setHovered((c) => (c === corner ? null : c));
  const toggle = (corner: Corner) => setPinned((p) => (p === corner ? null : corner));

  /**
   * Tapping a sector on the wheel. Below `lg` the four cards stack underneath the
   * wheel, so the sector you tap lights up a card several hundred pixels off
   * screen — take the reader to it (parked under the fixed 72px header). On
   * desktop the card is already sitting next to the sector, so nothing moves. A
   * tap on the card itself uses plain {@link toggle}: it is on screen already.
   */
  const selectFromWheel = (corner: Corner) => {
    const wasPinned = pinned === corner;
    toggle(corner);
    if (isDesktop || wasPinned) return;
    const card = cardRefs.current[corner];
    if (!card) return;
    const top = Math.max(0, card.getBoundingClientRect().top + window.scrollY - 88);
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => window.scrollTo({ top, behavior: smooth ? "smooth" : "auto" }));
  };
  const cardProps = (corner: Corner) => ({
    active: active === corner,
    onActivate: () => activate(corner),
    onDeactivate: () => deactivate(corner),
    onToggle: () => toggle(corner),
  });

  /**
   * Safety net for the outer ring: the CSS rotation below is the real thing, but
   * styled-jsx injects its <style> on the client, so a reload can land with the
   * ring's `animation-name` resolving to nothing — the ring then sits still
   * until some later repaint (switching tabs and back) kicks it. If no
   * animation is attached by the time we hydrate, drive the same 40s turn from
   * the Web Animations API, which needs no stylesheet at all. When the CSS did
   * take, this no-ops.
   */
  useEffect(() => {
    const ring = wheelRingRef.current;
    if (!ring || ring.getAnimations().length > 0) return;
    ring.animate([{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }], {
      duration: 40000,
      iterations: Infinity,
      easing: "linear",
    });
  }, []);

  return (
    // lg:pb matches the 80px the decorative frame extends below the card grid.
    // The section clips its own overflow, so without that padding the frame's
    // lower notch border is cut off — the CTA that used to sit here was what
    // gave the box its extra height. Only lg+ needs it (the frame is hidden
    // below lg).
    <section className="relative w-full overflow-hidden bg-background px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 md:pt-[70px] lg:pb-20">
      {/* 1300px column, same as the Q-Suite and SHRED sections above/below and as
          the footer — the old 1312px was built against a 64px artboard margin and
          left this section 6px wider than its neighbours. */}
      <div className="relative z-10 mx-auto max-w-[1300px]">
        <ScrollReveal direction="up" delay={0}>
          {/* The size ladder is spelled out instead of using headline-1:
              theme.css declares that class inside @layer utilities and emits it
              last, so it beat the old md:text-[55px] md:leading-[79px] step and
              the heading rendered 48px at every width while its Q-Suite / SHRED
              siblings went to 55px. The ladder now matches those siblings.

              font-bold, not the 600 headline-1 supplies: this was the only home
              section heading at 600 — Q-Suite, SHRED, Scarf and DarkSight all
              set font-bold — so it read a step lighter than everything around
              it. The colour was never the difference; text-highlight is #ffffff,
              the same white text-white gives the others. tracking-normal stays,
              which is the 0 tracking headline-1 wanted. */}
          <h2 className="m-0 text-center font-bold tracking-normal text-text-highlight text-[32px] leading-[40px] md:text-[44px] md:leading-[53px] lg:text-[55px] lg:leading-[79px]">
            HackGrid - Offensive Cyber Range
          </h2>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={100}>
          <p className="mx-auto mt-4 max-w-[640px] text-center text-[14px] leading-[20px] tracking-[0.25px] text-text-default md:text-[16px] md:leading-[28px] md:tracking-[0.5px]">
            Redefining cybersecurity training through realistic simulation and emulation. Sharpen
            practical skills, validate expertise, and tackle real-world challenges with confidence.
          </p>
        </ScrollReveal>

        {/* lg:mt is larger so the frame (which extends ~80px above the grid)
            clears the subtitle by ~43px like the design. */}
        <div className="relative mt-8 sm:mt-12 md:mt-16 lg:mt-[120px]">
          {/* Decorative high-tech frame (Figma "Decor Asset" 1833:35471): a fixed
              1912px-wide box (texture + angular notch borders top & bottom),
              centred and clipped by the section so the notch/corners keep their
              design proportions. It sits just outside the card grid. */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 z-0 hidden h-[calc(100%+160px)] w-[1912px] -translate-x-1/2 -translate-y-1/2 lg:block"
          >
            <div className="absolute left-1/2 top-0 h-full w-[1440px] -translate-x-1/2">
              <Image
                src="/images/Home/hackgrid-bg.png"
                alt=""
                fill
                sizes="1440px"
                className="object-cover object-bottom opacity-20"
              />
            </div>
            <div className="absolute inset-x-0 top-0 h-[81px]">
              <Image src="/images/Home/hackgrid-frame.svg" alt="" fill className="object-fill" />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-[81px] -scale-y-100">
              <Image src="/images/Home/hackgrid-frame.svg" alt="" fill className="object-fill" />
            </div>
          </div>

          {/* Below lg the wheel sits on top and the four cards stack full-width
              in reading order (top-left, top-right, bottom-left, bottom-right).
              That interleaves the two desktop columns, so the cards are placed
              one by one instead of being wrapped in a left and a right stack —
              lg+ puts them back into [cards | wheel | cards] with the wheel
              spanning both card rows. */}
          <div className="relative z-10 grid grid-cols-1 items-center gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-x-10 lg:gap-y-[77px]">
            <ConnectorOverlay hovered={active} />

            <ScrollReveal
              direction="up"
              delay={250}
              className="order-1 mb-4 lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mb-0"
            >
              {/* Below md the wheel takes the full content width (358px at a 390px
                screen, capped at 420px) — the sector labels are baked into the
                artwork at 78% of this box, so a smaller wheel renders them too
                small to read on a phone. md+ keeps the design's fixed 426px. */}
              <div className="relative mx-auto aspect-square w-full max-w-[420px] md:h-[426px] md:w-[426px] md:max-w-none">
                <div
                  ref={wheelRingRef}
                  className="hg-wheel-spin absolute inset-0 pointer-events-none"
                >
                  <Image
                    src="/images/Home/Hackgrid_circle.svg"
                    alt=""
                    fill
                    sizes="(max-width: 768px) 90vw, 426px"
                    /* Eager like the product page's copy: a lazy ring can decode
                     after the layer is composited, which leaves the artwork
                     pinned at its first frame even though the rotation is
                     running underneath. */
                    loading="eager"
                    className="object-contain opacity-90"
                    aria-hidden
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* The box carries the artwork's own aspect ratio so nothing is
                    letterboxed: the <img> and the WheelSectors overlay then map
                    the viewBox onto the exact same rect. In a square box they
                    each letterbox on their own and round differently, which
                    slid the overlay ~0.5 units off the baked-in labels. */}
                  <div className="relative w-[78%]" style={{ aspectRatio: "344 / 319" }}>
                    <Image
                      src="/images/Home/Hackgrid_core.svg"
                      alt="HackGrid Red Team and Blue Team"
                      fill
                      className="object-contain"
                    />
                    <WheelSectors
                      active={active}
                      onActivate={activate}
                      onDeactivate={deactivate}
                      onToggle={selectFromWheel}
                    />
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal
              direction="left"
              delay={200}
              className="order-2 lg:order-none lg:col-start-1 lg:row-start-1"
            >
              {/* The ref target for a tap on the matching wheel sector. */}
              <div
                ref={(el) => {
                  cardRefs.current.topLeft = el;
                }}
              >
                <FeatureCard {...FEATURES.topLeft} {...cardProps("topLeft")} />
              </div>
            </ScrollReveal>
            <ScrollReveal
              direction="right"
              delay={200}
              className="order-3 lg:order-none lg:col-start-3 lg:row-start-1"
            >
              <div
                ref={(el) => {
                  cardRefs.current.topRight = el;
                }}
              >
                <FeatureCard {...FEATURES.topRight} {...cardProps("topRight")} />
              </div>
            </ScrollReveal>
            <ScrollReveal
              direction="left"
              delay={300}
              className="order-4 lg:order-none lg:col-start-1 lg:row-start-2"
            >
              <div
                ref={(el) => {
                  cardRefs.current.bottomLeft = el;
                }}
              >
                <FeatureCard {...FEATURES.bottomLeft} {...cardProps("bottomLeft")} />
              </div>
            </ScrollReveal>
            <ScrollReveal
              direction="right"
              delay={300}
              className="order-5 lg:order-none lg:col-start-3 lg:row-start-2"
            >
              <div
                ref={(el) => {
                  cardRefs.current.bottomRight = el;
                }}
              >
                <FeatureCard {...FEATURES.bottomRight} {...cardProps("bottomRight")} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* The rotation is applied from a class in this same block rather than an
           inline style="animation:...": an inline animation names a keyframes
           rule that styled-jsx has not injected yet, so the ring could come up
           dead and only start spinning after an unrelated repaint. will-change
           keeps it on its own compositor layer, so the frames keep coming
           instead of stalling on a stale raster. No reduced-motion opt-out here
           on purpose — this ring is meant to always turn. */
        .hg-wheel-spin {
          animation: hackgridSpin 40s linear infinite;
          will-change: transform;
        }
        @keyframes hackgridSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
}
